import React, { useState, useEffect } from 'react'
import { Breadcrumbs, Typography, ThemeProvider } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import UserLocationForm from './announceForms/announceLocationForm'
import UserSchedulesForm from './announceForms/announceSchedulesForm'
import UserPossibilitiesForm from './announceForms/announcePossibilitiesForm'
import UserContactForm from './announceForms/announceContactForm'
import UserPricesForm from './announceForms/announcePricesForm'
import UserServicesForm from './announceForms/announceServicesForm'
import theme from '../../themes/theme'
import { useTranslation } from 'react-i18next'
import MyButton from '../../components/buttons'
import ImageUpload from './announceForms/announcePicturesForm'
import {
  postMyAnnounce,
  updateMyAnnounce,
  fetchAnnounceByUserId,
  updateLocalStorageWithUserAnnounce,
} from '../../../services/announces'
import UserTitleForm from './announceForms/announceTitle'
import Modal from '../../components/MyModal'
import {
  validateFormData,
  ValidationError,
} from '../../validators/postAnnounce'
import { IoIosWarning } from 'react-icons/io'
import { AlertCircle, AlertTriangle, CheckCircle, Send } from 'lucide-react'
import CookieManager from '../../lib/CookiesManager'
import StepSelector from '../../components/StepSelector'
import { title } from 'process'

interface Field {
  type: string
  label: string
  options?: string[]
}

interface Step {
  title: string
  fields: Field[]
}

const FormWizard: React.FC<{ userProfileFormProps: any }> = ({
  userProfileFormProps,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1335)
  const [searchParams, setSearchParams] = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [activeStep, setActiveStep] = useState(() => {
    const stepParam = searchParams.get('step')
    return stepParam ? parseInt(stepParam, 10) : 0
  })
  const navigate = useNavigate()
  const requiredFields = {
    email: '',
    userId: null,
    status: [],
    escort: false,
    isAvailable: false,
    smsOnly: false,
    callOnly: false,
    whatsapp: false,
    emailOnly: false,
    title: '',
    description: '',
    boosted: false,
    practices: [],
    price: [],
    timing: [],
    priceEscort: [],
    timingEscort: [],
  }

  const [formData, setFormData] = useState(() => {
    const initialData = userProfileFormProps || {}
    const myAnnounceStr = localStorage.getItem('myAnnounce')
    const myAnnounce = myAnnounceStr ? JSON.parse(myAnnounceStr) : null

    return myAnnounce && myAnnounce.hasOwnProperty('nickname')
      ? {
          ...initialData,
          ...myAnnounce,
          boostedTime: 'null',
          boosted: false,
          practices: [],
          isAvailable: false,
        }
      : {
          ...initialData,
          boostedTime: 'null',
          boosted: false,
          practices: [],
          isAvailable: false,
        }
  })
  useEffect(() => {
    const checkLocalStorage = () => {
      const myAnnounceStr = localStorage.getItem('myAnnounce')
      let myAnnounce = myAnnounceStr ? JSON.parse(myAnnounceStr) : {}

      let missingFieldsList: string[] = []
      let updatedAnnounce = { ...myAnnounce }

      Object.entries(requiredFields).forEach(([key, defaultValue]) => {
        if (!(key in myAnnounce) || myAnnounce[key] === null) {
          updatedAnnounce[key] = defaultValue
          missingFieldsList.push(key)
        }
      })

      setFormData((prev: any) => ({ ...prev, ...updatedAnnounce }))
      setMissingFields(missingFieldsList)

      if (missingFieldsList.length > 0) {
        localStorage.setItem('myAnnounce', JSON.stringify(updatedAnnounce))
      }
    }

    checkLocalStorage()
  }, [])

  const [isStepValid, setIsStepValid] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [, setStepValidity] = useState<boolean[]>([])
  const { t } = useTranslation()
  const [hasExistingAnnounce, setHasExistingAnnounce] = useState(false)
  const [popupMessage, setPopupMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [submissionStatus, setSubmissionStatus] = useState<
    'success' | 'error' | null
  >(null)

  const steps: Step[] = [
    {
      title: t('create-ad.my-services'),
      fields: [{ type: 'table', label: 'status' }],
    },
    {
      title: t('create-ad.location'),
      fields: [{ type: 'input', label: 'location' }],
    },
    {
      title: t('create-ad.schedules'),
      fields: [],
    },
    {
      title: t('create-ad.possibilities'),
      fields: [],
    },
    {
      title: t('create-ad.contact'),
      fields: [],
    },
    {
      title: t('create-ad.prices'),
      fields: [],
    },
    {
      title: t('create-ad.announce'),
      fields: [
        { type: 'input', label: 'title' },
        { type: 'textarea', label: 'description' },
      ],
    },
    {
      title: t('create-ad.pictures'),
      fields: [],
    },
  ]

  useEffect(() => {
    setSearchParams({ step: activeStep.toString() })
  }, [activeStep])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const stepParam = searchParams.get('step')
    if (stepParam) {
      const step = parseInt(stepParam, 10)
      if (step >= 0 && step < steps.length) {
        setActiveStep(step)
      }
    }
  }, [searchParams])

  useEffect(() => {
    const validateCurrentStep = () => {
      const isValid = steps[activeStep].fields.every((field: any) => {
        const fieldValue = formData[field.label]
        if (field.label === 'weight') {
          return (
            typeof fieldValue === 'number' &&
            fieldValue >= 40 &&
            fieldValue <= 120
          )
        } else if (field.label === 'height') {
          return (
            typeof fieldValue === 'number' &&
            fieldValue >= 140 &&
            fieldValue <= 220
          )
        } else if (field.label === 'nickname') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 3
        } else if (field.label === 'title') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 4
        } else if (field.label === 'description') {
          return (
            typeof fieldValue === 'string' && fieldValue.trim().length > 129
          )
        } else if (field.label === 'languages') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (field.label === 'location') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (field.label === 'nationality') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 0
        } else if (field.label === 'status') {
          return Array.isArray(fieldValue) && fieldValue?.length > 0
        } else if (
          field.label === 'available247' ||
          field.label === 'customHours'
        ) {
          return formData['available247'] || formData['customHours']
        } else {
          return fieldValue === true || fieldValue === false
            ? true
            : !!fieldValue
        }
      })
      setIsStepValid(isValid)
      setStepValidity(prev => {
        const newValidity = [...prev]
        newValidity[activeStep] = isValid
        return newValidity
      })
    }

    validateCurrentStep()
  }, [activeStep, formData])

  useEffect(() => {
    let isMounted = true

    const checkLocalStorage = () => {
      const myAnnounceStr = localStorage.getItem('myAnnounce')
      if (myAnnounceStr && isMounted) {
        const myAnnounce = JSON.parse(myAnnounceStr)
        setFormData((prev: any) => ({ ...prev, ...myAnnounce }))
      }
    }

    const fetchExistingAnnounce = async () => {
      const token = CookieManager.getToken()
      const userId = CookieManager.getUserId()
      if (token && userId) {
        try {
          const myAnnounce = await fetchAnnounceByUserId(userId, token)
          if (myAnnounce && isMounted) {
            setHasExistingAnnounce(true)
          }
        } catch (error) {
          if (isMounted) {
            Sentry.captureException(error, {
              extra: {
                action: 'fetchExistingAnnounce',
                userId,
                timestamp: new Date().toISOString(),
              },
              tags: {
                type: 'api_error',
                method: 'fetchExistingAnnounce',
              },
            })
          }
        }
      }
    }

    checkLocalStorage()
    fetchExistingAnnounce()

    return () => {
      isMounted = false
    }
  }, [])

  const handleBreadcrumbClick = (index: number) => {
    if (activeStep < index && !isStepValid) {
      return
    }
    setActiveStep(index)
  }

  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const newStep = activeStep - 1
    if (newStep >= 0) {
      setActiveStep(newStep)
      setSearchParams({ step: newStep.toString() })
    }
  }

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const newStep = activeStep + 1
    if (newStep < steps.length) {
      setActiveStep(newStep)
      setSearchParams({ step: newStep.toString() })
    }
  }

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    const token = CookieManager.getToken()
    const userId = CookieManager.getUserId()

    if (!token || !userId) {
      setPopupMessage(t('errors.auth-required'))
      setSubmissionStatus('error')
      setIsModalOpen(true)
      return
    }

    // 🔍 Récupération des données depuis localStorage
    const myAnnounceStr = localStorage.getItem('myAnnounce')
    if (!myAnnounceStr) {
      console.error('❌ Aucune annonce trouvée dans localStorage.')
      setPopupMessage(t('errors.no-announce-found'))
      setSubmissionStatus('error')
      setIsModalOpen(true)
      return
    }

    const myAnnounce = JSON.parse(myAnnounceStr)

    // 📌 Vérifier les champs obligatoires avant d'envoyer
    let errors: ValidationError[] = []

    if (!myAnnounce.title || myAnnounce.title.trim().length < 5) {
      errors.push({
        stepName: t('create-ad.announce'),
        message: t('title-required'),
        field: '',
      })
    }

    if (!myAnnounce.description || myAnnounce.description.trim().length < 130) {
      errors.push({
        stepName: t('create-ad.announce'),
        message: t('description-required'),
        field: '',
      })
    }

    if (errors.length > 0) {
      setValidationErrors(errors)
      setShowValidationErrors(true)
      setPopupMessage(t('create-ad.form-validation-failed'))
      setSubmissionStatus('error')
      setIsModalOpen(true)
      setIsSubmitting(false)
      return
    }

    console.log(
      '📤 Données envoyées au backend :',
      JSON.stringify(myAnnounce, null, 2),
    )

    try {
      const existingAnnounce = await fetchAnnounceByUserId(userId, token)

      if (existingAnnounce && existingAnnounce.id) {
        await updateMyAnnounce(userId, token, myAnnounce)
        setPopupMessage(t('announce-successfully-updated'))
      } else {
        await postMyAnnounce(myAnnounce, token, userId)
        setPopupMessage(t('announce-successfully-created'))
      }

      // ✅ Nettoyage du localStorage après succès
      localStorage.removeItem('myAnnounce')

      setSubmissionStatus('success')
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'handleSubmit',
          userId,
          timestamp: new Date().toISOString(),
        },
        tags: { type: 'api_error', method: 'handleSubmit' },
      })
      console.error("❌ Erreur lors de l'envoi :", error)

      setPopupMessage(t('announce-error-updated'))
      setSubmissionStatus('error')
    } finally {
      setIsModalOpen(true)
      setIsSubmitting(false)
    }
  }

  const renderValidationErrors = () => {
    if (!showValidationErrors || validationErrors.length === 0) return null

    return (
      <div className='bg-redlight p-4 rounded-lg mb-4'>
        <ul className='space-y-2'>
          {validationErrors.map((error, index) => (
            <li
              key={index}
              className='text-anthracite text-left flex items-start'
            >
              <IoIosWarning
                className='text-redpink flex-shrink-0 mt-1 mr-2'
                size={16}
              />
              <span>
                <b>{error.stepName} :</b> {error.message}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }))
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    if (submissionStatus === 'success') {
      setSearchParams({})
      navigate('/dashboard/announces/')
    }
    window.location.reload()
  }

  const renderBreadcrumbs = () => {
    if (isMobile) {
      return (
        <StepSelector
          steps={steps}
          activeStep={activeStep}
          onStepSelect={handleBreadcrumbClick}
          isStepValid={isStepValid}
        />
      )
    }

    return (
      <div className='w-full overflow-hidden mb-2'>
        <div className='overflow-x-auto md:overflow-visible '>
          <Breadcrumbs
            aria-label='breadcrumb'
            separator=''
            className='bg-anthracite rounded-lg min-w-fit'
          >
            <ol className='list-none flex w-full bg-white rounded-lg whitespace-nowrap'>
              {steps.map((step, index) => (
                <li
                  key={step.title}
                  className={`breadcrumb-item mr-[-18px] text-[11px] sm:text-xs pr-2 sm:pr-4 md:pr-6 items-center text-center  cursor-pointer py-2 md:py-3 text-xs md:text-sm ${
                    activeStep >= index
                      ? 'bg-redpink text-white'
                      : 'bg-anthracite text-white'
                  }`}
                  style={{
                    clipPath:
                      index !== 0
                        ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)'
                        : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 0px 50%)',
                    borderRadius:
                      index === 0
                        ? '8px 0 0 8px'
                        : index === steps.length - 1
                        ? '0 8px 8px 0'
                        : 'none',
                    paddingLeft: index === 0 ? '16px' : '32px',
                  }}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  <Typography>
                    <span className='mr-1 md:mr-2 font-bold'>{index + 1}.</span>
                    {step.title}
                  </Typography>
                </li>
              ))}
            </ol>
          </Breadcrumbs>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full mb-20'>
      {renderBreadcrumbs()}

      <ThemeProvider theme={theme}>
        <form className='mt-2 mb-5'>
          {activeStep === 0 && (
            <UserServicesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 1 && (
            <UserLocationForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 2 && (
            <UserSchedulesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 3 && (
            <UserPossibilitiesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 4 && (
            <UserContactForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 5 && (
            <UserPricesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 6 && (
            <UserTitleForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 7 && <ImageUpload />}
          <div className='bottom-0 left-0 fixed mt-2 w-full bg-white p-2 shadow-lg sm:shadow-none sm:static sm:p-0 z-40'>
            <div
              className={`max-w-7xl mx-auto flex flex-row space-x-2 w-full ${
                activeStep === 0 ? 'justify-end' : 'justify-between'
              }`}
            >
              {/* Bouton "Précédent" sauf sur la première étape */}
              {activeStep !== 0 && (
                <MyButton
                  className='w-full sm:w-auto'
                  variant='redpink-anthracite'
                  onClick={handleBack}
                >
                  {t('create-ad.previous')}
                </MyButton>
              )}

              {/* Bouton "Modifier l'annonce" affiché sur toutes les étapes */}
              {/* Bouton "Modifier l'annonce" affiché sur toutes les étapes sauf la dernière */}
              {/* Si une annonce existe, on affiche "Modifier l'annonce" sur toutes les étapes y compris la dernière */}
              {/* Si une annonce existe, "Modifier l'annonce" est toujours affiché sur toutes les étapes */}
              {hasExistingAnnounce && (
                <MyButton
                  className='w-full sm:w-auto'
                  variant='green'
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className='flex items-center justify-center gap-2'>
                      <svg
                        className='animate-spin h-5 w-5 text-white'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                      {t('saving')}
                    </div>
                  ) : (
                    t('create-ad.modify-ad')
                  )}
                </MyButton>
              )}

              {/* Bouton "Créer l'annonce" affiché SEULEMENT sur la dernière étape et si l'annonce n'existe pas */}
              {!hasExistingAnnounce && activeStep === steps.length - 1 && (
                <MyButton
                  className='w-full sm:w-auto'
                  variant={isStepValid ? 'green' : 'redpink'}
                  disabled={!isStepValid}
                  onClick={handleSubmit}
                >
                  {t('create-ad.post-ad')}
                </MyButton>
              )}

              {/* Bouton "Suivant" affiché sur toutes les étapes sauf la dernière */}
              {activeStep < steps.length - 1 && (
                <MyButton
                  className='w-full sm:w-auto'
                  variant={isStepValid ? 'redpink-anthracite' : 'redpink'}
                  disabled={!isStepValid}
                  onClick={handleNext}
                >
                  {t('create-ad.next')}
                </MyButton>
              )}
            </div>
          </div>
        </form>
      </ThemeProvider>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size='medium'>
        <div className='bg-white rounded-lg overflow-hidden p-4'>
          <div className='text-anthracite px-3 mb-3 flex items-center justify-center space-x-2'>
            {submissionStatus === 'success' ? (
              <div className='rounded-full flex items-center justify-center'>
                <CheckCircle className='w-6 h-6 text-whatsapp' />
              </div>
            ) : submissionStatus === 'error' ? (
              <div className='w-12 h-12 rounded-full bg-redpink/20 flex items-center justify-center'>
                <AlertCircle className='w-8 h-8 text-redpink' />
              </div>
            ) : (
              <div className='w-12 h-12 rounded-full bg-white/20 flex items-center justify-center'>
                <Send className='w-8 h-8 text-white' />
              </div>
            )}
            <h2 className='text-lg font-semibold'>{t('announce-status')}</h2>
          </div>

          <div className='space-y-4'>
            {submissionStatus === 'error' && validationErrors.length > 0 ? (
              <div className='space-y-4'>
                <div className='flex items-center space-x-3 bg-redlight/10 p-4 rounded-lg border border-redpink/20'>
                  <AlertTriangle className='w-6 h-6 text-redpink flex-shrink-0' />
                  <p className='text-anthracite font-medium'>
                    {t('create-ad.form-validation-failed')}
                  </p>
                </div>

                {renderValidationErrors()}
              </div>
            ) : (
              <div
                className={`flex items-center space-x-3 p-2 rounded-lg ${
                  submissionStatus === 'success'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-redlight border border-redpink/20'
                }`}
              >
                <p
                  className={`font-medium ${
                    submissionStatus === 'success'
                      ? 'text-green-800'
                      : 'text-redpink'
                  }`}
                >
                  {submissionStatus === 'success'
                    ? t('announce-successfully-updated')
                    : t('announce-error-updated')}
                </p>
              </div>
            )}
            {missingFields.length > 0 && (
              <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 text-yellow-800 mb-4'>
                <AlertTriangle className='inline-block w-5 h-5 mr-2' />
                <strong>{t('missing-fields-warning')}</strong>
                <ul className='mt-2 list-disc pl-5'>
                  {missingFields.map((field, index) => (
                    <li key={index}>{t(`fields.${field}`)}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className='flex justify-end mt-6'>
              <button
                onClick={handleCloseModal}
                className={`flex items-center space-x-2 px-6 py-2.5 rounded-lg transition-colors ${
                  submissionStatus === 'error' && validationErrors.length > 0
                    ? 'bg-redpink text-white hover:bg-redpink/90'
                    : 'bg-anthracite text-white hover:bg-anthracite/90'
                }`}
              >
                {submissionStatus === 'error' && validationErrors.length > 0 ? (
                  <>
                    <AlertCircle className='w-4 h-4' />
                    <span>{t('create-ad.correct-errors')}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className='w-4 h-4' />
                    <span>{t('close')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default FormWizard
